import React, { useState, useEffect } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../lib/convex';
import { Link, useParams } from 'react-router-dom';
import { Calendar, Tag, ArrowRight, Search, Loader2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Footerdemo } from '../ui/footer-section';
import { SEOHead } from '@/lib/seo';
import ReactMarkdown from 'react-markdown';
import DOMPurify from 'dompurify';

export default function BlogList() {
  const { tag } = useParams<{ tag?: string }>();
  const [searchQuery, setSearchQuery] = useState('');
  
  // Sanitize tag parameter
  const sanitizedTag = tag ? DOMPurify.sanitize(tag) : undefined;
  
  const allPosts = useQuery(api.blog.getPublishedBlogPosts);
  const taggedPosts = useQuery(
    api.blog.getPublishedBlogPosts, 
    sanitizedTag ? { tag: sanitizedTag } : 'skip'
  );
  const allTags = useQuery(api.blog.getBlogTags);
  
  // Determine which posts to display
  const posts = sanitizedTag ? taggedPosts : allPosts;
  
  // Filter posts based on search query
  const filteredPosts = React.useMemo(() => {
    if (!posts) return [];
    
    if (!searchQuery.trim()) return posts;
    
    // Sanitize search query
    const sanitizedQuery = DOMPurify.sanitize(searchQuery.toLowerCase());
    
    return posts.filter(post => 
      post.title.toLowerCase().includes(sanitizedQuery) ||
      post.summary.toLowerCase().includes(sanitizedQuery) ||
      post.tags.some(tag => tag.toLowerCase().includes(sanitizedQuery))
    );
  }, [posts, searchQuery]);
  
  // Reset search when changing tag
  useEffect(() => {
    setSearchQuery('');
  }, [tag]);

  // Sanitize input value
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Limit length to prevent DoS attacks via very long queries
    const value = e.target.value.slice(0, 100);
    setSearchQuery(value);
  };
  
  // Loading state
  if (!posts) {
    return (
      <div className="min-h-screen flex flex-col pt-16">
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
        <Footerdemo />
      </div>
    );
  }
  
  // Safe tag name for display
  const displayTag = sanitizedTag ? DOMPurify.sanitize(sanitizedTag) : '';
  
  return (
    <div className="min-h-screen flex flex-col pt-16">
      <SEOHead
        title={sanitizedTag 
          ? `Posts tagged with "${displayTag}"`
          : 'Blog - AI Content Generation Tips & Strategies'
        }
        description={sanitizedTag 
          ? `Browse all our articles tagged with "${displayTag}" about AI content generation, SEO, and marketing.`
          : 'Learn about AI content generation, SEO optimization, and marketing strategies. Get tips, tutorials, and insights to create better content that drives results.'
        }
        type="website"
        canonicalUrl={`https://aiseobloggenerator.com${sanitizedTag ? `/blog/tag/${sanitizedTag}` : '/blog'}`}
      />
      
      <div className="bg-gradient-to-b from-blue-100 to-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl font-bold mb-4">
              {sanitizedTag 
                ? `Posts Tagged: ${displayTag}`
                : 'Blog'
              }
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              {sanitizedTag 
                ? `Explore all our articles related to ${displayTag}`
                : 'Insights on AI content generation, SEO, and content marketing strategies'
              }
            </p>
            
            <div className="relative max-w-xl mx-auto">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <Input
                type="text"
                placeholder="Search posts..."
                value={searchQuery}
                onChange={handleSearchChange}
                className="pl-10"
                aria-label="Search blog posts"
                maxLength={100}
              />
            </div>
          </div>
        </div>
      </div>
      
      <div className="flex-1 max-w-7xl mx-auto px-4 py-12">
        <div className="grid md:grid-cols-12 gap-8">
          <div className="md:col-span-9">
            {sanitizedTag && (
              <div className="mb-8">
                <Link to="/blog">
                  <Button variant="outline" size="sm">
                    ← All Posts
                  </Button>
                </Link>
              </div>
            )}
            
            {filteredPosts.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <h2 className="text-2xl font-bold text-gray-700 mb-2">No posts found</h2>
                <p className="text-gray-600">
                  {searchQuery 
                    ? 'No posts match your search query.' 
                    : sanitizedTag 
                      ? `No posts found with the tag "${displayTag}".`
                      : 'No blog posts have been published yet.'
                  }
                </p>
              </div>
            ) : (
              <div className="space-y-8">
                {filteredPosts.map((post) => {
                  // Sanitize post data
                  const safeTitle = DOMPurify.sanitize(post.title);
                  const safeSummary = DOMPurify.sanitize(post.summary);
                  const safeImageUrl = post.imageUrl ? DOMPurify.sanitize(post.imageUrl) : undefined;
                  const safeSlug = DOMPurify.sanitize(post.slug);
                  const safeTags = post.tags.map(tag => DOMPurify.sanitize(tag));
                  
                  return (
                    <article 
                      key={post._id} 
                      className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow"
                    >
                      <div className="md:flex">
                        {safeImageUrl && (
                          <div className="md:w-1/3 h-48 md:h-auto relative">
                            <img 
                              src={safeImageUrl} 
                              alt={safeTitle}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <div className={`p-6 ${safeImageUrl ? 'md:w-2/3' : 'w-full'}`}>
                          <div className="flex flex-wrap gap-2 mb-3">
                            {safeTags.slice(0, 3).map((tag, index) => (
                              <Link 
                                key={index} 
                                to={`/blog/tag/${tag}`}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors"
                              >
                                <Tag className="w-3 h-3 mr-1" />
                                {tag}
                              </Link>
                            ))}
                            {post.tags.length > 3 && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                +{post.tags.length - 3}
                              </span>
                            )}
                          </div>
                          
                          <h2 className="text-xl font-bold mb-2">
                            <Link 
                              to={`/blog/${safeSlug}`}
                              className="hover:text-blue-600 transition-colors"
                            >
                              {safeTitle}
                            </Link>
                          </h2>
                          
                          <div className="prose prose-sm mb-4 line-clamp-3">
                            <ReactMarkdown
                              components={{
                                p: ({children}) => (
                                  <p className="text-gray-600">{children}</p>
                                ),
                              }}
                            >
                              {safeSummary}
                            </ReactMarkdown>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <div className="text-sm text-gray-500 flex items-center">
                              <Calendar className="w-4 h-4 mr-1" />
                              {post.publishedAt && formatDistanceToNow(post.publishedAt, { addSuffix: true })}
                            </div>
                            
                            <Link to={`/blog/${safeSlug}`}>
                              <Button variant="link" className="p-0 h-auto">
                                Read More
                                <ArrowRight className="w-4 h-4 ml-1" />
                              </Button>
                            </Link>
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </div>
          
          <div className="md:col-span-3">
            <div className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
              <h3 className="text-lg font-bold mb-4">Categories</h3>
              
              <div className="space-y-2">
                <Link 
                  to="/blog"
                  className={`block px-3 py-2 rounded-md ${!sanitizedTag ? 'bg-blue-100 text-blue-800' : 'hover:bg-gray-100'} transition-colors`}
                >
                  All Posts
                </Link>
                
                {allTags?.map((tagName, index) => {
                  const safeTagName = DOMPurify.sanitize(tagName);
                  return (
                    <Link 
                      key={index} 
                      to={`/blog/tag/${safeTagName}`}
                      className={`block px-3 py-2 rounded-md ${sanitizedTag === safeTagName ? 'bg-blue-100 text-blue-800' : 'hover:bg-gray-100'} transition-colors`}
                    >
                      {safeTagName}
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footerdemo />
    </div>
  );
}